<template>
  <transition name="slide">
    <div v-if="show" class="base-drawer-overlay" @click.self="$emit('close')">
      <!-- bare: host a component that brings its own header/body/footer (e.g. a
           reused drawer-view). Keeps the overlay/slide/dvh/safe-area shell but
           skips this component's chrome so there's no double header. -->
      <div v-if="bare" class="base-drawer" :style="{ width: width }">
        <slot></slot>
      </div>
      <div v-else class="base-drawer" :style="{ width: width }">
        <!-- Header -->
        <div class="base-drawer__header">
          <div class="base-drawer__header-top">
            <div class="base-drawer__title-wrap">
              <slot name="title-prefix"></slot>
              <h4 class="base-drawer__title">{{ title }}</h4>
            </div>
            <div class="base-drawer__header-actions">
              <slot name="header-actions"></slot>
              <div class="base-drawer__close-btn" @click="$emit('close')">
                <i class="tim-icons icon-simple-remove"></i>
              </div>
            </div>
          </div>
          <slot name="header-extra"></slot>
        </div>

        <!-- Body -->
        <div class="base-drawer__body">
          <slot></slot>
        </div>

        <!-- Footer -->
        <div v-if="$slots.footer" class="base-drawer__footer">
          <slot name="footer"></slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'BaseDrawer',
  props: {
    show: { type: Boolean, default: false },
    title: { type: String, default: '' },
    width: { type: String, default: '520px' },
    // Render only the sliding panel (no header/body/footer chrome) so a hosted
    // component can supply its own full layout. See AddItemView reuse.
    bare: { type: Boolean, default: false }
  },
  watch: {
    show(val) {
      document.body.style.overflow = val ? 'hidden' : ''
    }
  },
  beforeDestroy() {
    document.body.style.overflow = ''
  }
}
</script>

<style lang="scss" scoped>
.base-drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1050;
}

.base-drawer {
  position: fixed;
  top: 0;
  /* If the wizard side panel is open, it sets --wizard-panel-offset on :root so
     this drawer slides left of it instead of being hidden behind it. Fallback is 0. */
  right: var(--wizard-panel-offset, 0);
  transition: right 0.3s ease;
  // 100vh includes mobile browser chrome (address bar / bottom toolbar) —
  // the footer would get pushed below the visible area. 100dvh (dynamic
  // viewport height) accounts for chrome. 100vh stays as fallback for older
  // browsers (iOS <15.4, Chrome <108) which will ignore the dvh declaration.
  height: 100vh;
  height: 100dvh;
  background: #171826;
  display: flex;
  flex-direction: column;
  z-index: 1051;
  box-shadow: -10px 0 40px rgba(0, 0, 0, 0.3);

  @media (max-width: 1200px) {
    width: 450px !important;
  }

  @media (max-width: 768px) {
    width: 100% !important;
    max-width: 100vw;
  }
}

.base-drawer__header {
  flex-shrink: 0;
  padding: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  background: linear-gradient(180deg, #2d2d44 0%, #252538 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.base-drawer__header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.base-drawer__title-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.base-drawer__title {
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.02em;
  margin: 0;
}

.base-drawer__header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.base-drawer__body {
  flex: 1 1 auto;
  // min-height: 0 is required for flex children with overflow on iOS Safari
  // — without it, the body expands to fit content and shoves the footer out.
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px 24px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.02); }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
}

.base-drawer__footer {
  flex-shrink: 0;
  padding: 16px 24px;
  // Respect iOS home-indicator / device safe area so CTAs aren't hidden
  // behind the bottom gesture bar.
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
}

// Slide transition
.slide-enter-active,
.slide-leave-active {
  transition: opacity 0.3s ease;

  .base-drawer {
    transition: transform 0.3s ease;
  }
}

.slide-enter,
.slide-leave-to {
  opacity: 0;

  .base-drawer {
    transform: translateX(100%);
  }
}
</style>

<style lang="scss">
// Header icon buttons. Unscoped so a consumer's buttons in the header-actions
// slot can use .base-drawer__header-btn and match the built-in close button.
.base-drawer__close-btn,
.base-drawer__header-btn {
  width: 40px;
  height: 40px;
  padding: 0;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  i { font-size: 12px; }
}

// Label styles (unscoped to reach slotted content)
.base-drawer label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
}

// Light mode
.white-content .base-drawer {
  background: #ffffff;
  box-shadow: -10px 0 40px rgba(0, 0, 0, 0.1);
}

.white-content .base-drawer label {
  color: #344675;
}

.white-content .base-drawer__header {
  border-bottom-color: #e3e3e3;
  background: linear-gradient(180deg, #f8f9fa 0%, #f5f6fa 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.white-content .base-drawer__title {
  color: #344675;
}

.white-content .base-drawer__close-btn,
.white-content .base-drawer__header-btn {
  background: #f5f6fa;
  border-color: #e3e3e3;
  color: #9A9A9A;

  &:hover:not(:disabled) {
    background: #e9ecef;
    color: #344675;
  }
}

.white-content .base-drawer__body {
  &::-webkit-scrollbar-track { background: #f5f6fa; }
  &::-webkit-scrollbar-thumb { background: rgba(0, 0, 0, 0.15); }
}

.white-content .base-drawer__footer {
  border-top-color: #e3e3e3;
  background: #f8f9fa;
}

.white-content .base-drawer .text-white {
  color: #344675 !important;
}
</style>
